import { QueryClient } from '@tanstack/react-query';
import { ITrainingSet } from '../types/my-types';

const isCertified = (client: QueryClient, trainingSetId: string) =>
  !!client.getQueryData<ITrainingSet>(['trainingSet', trainingSetId])?.certified_at;

/**
 * Drops the certification from the cached training set, without a refetch.
 *
 * Saving or deleting a mark always withdraws certification server-side, so the
 * outcome is known without asking. Marks save constantly during a marking
 * session, which is why this one case is worth not re-fetching for.
 *
 * The only revocation rule the UI reproduces; every other edit uses
 * refreshCertification(). The next fetch is authoritative regardless.
 */
export const clearCachedCertification = async (client: QueryClient, trainingSetId?: string) => {
  // An uncertified set has nothing to withdraw. Returning early also keeps the
  // cached object identical by reference, so marking does not re-render every
  // subscriber on each save.
  if (!trainingSetId || !isCertified(client, trainingSetId)) {
    return;
  }

  // A fetch already in flight carries the pre-revocation certified_at, and
  // would land after this write and undo it.
  await client.cancelQueries({ queryKey: ['trainingSet', trainingSetId] });

  client.setQueryData<ITrainingSet>(['trainingSet', trainingSetId], (previous) =>
    previous ? { ...previous, certified_at: null, certified_by: null } : previous
  );
};

/**
 * Refetches the training set so the server decides whether this edit withdrew
 * its certification.
 *
 * Only certified sets need asking; an uncertified one has nothing to lose.
 */
export const refreshCertification = (client: QueryClient, trainingSetId?: string) => {
  if (!trainingSetId || !isCertified(client, trainingSetId)) {
    return;
  }

  client.invalidateQueries({ queryKey: ['trainingSet', trainingSetId] });
};
